import {environment} from '../../../environments/environment';
import {phoneCode, swappedPhoneCode} from '../../pages/full-pages/phone_codes';
import {countryCurrencyContent} from '../../pages/full-pages/invoiceslip/currencySymbols';

/**
 * Country handling for screens.
 *
 * The tenant's organisation-group country (localStorage 'organizationGroup') wins once it
 * is loaded. Before that (login, OTP, kiosk start) the build's `environment.defaultCountry`
 * is used: 'AU' for the .au builds, 'IN' otherwise.
 */

export interface CountryLocale {
  countryIso: string;
  dialCode: string;
  timeZone: string;
  locale: string;
  currency: string;
  currencySymbol: string;
}

const TIME_ZONES: Record<string, string> = {
  IN: 'Asia/Kolkata',
  AU: 'Australia/Sydney',
  NZ: 'Pacific/Auckland',
  SG: 'Asia/Singapore',
  GB: 'Europe/London',
  US: 'America/New_York',
};

const LOCALES: Record<string, string> = {
  IN: 'en-IN',
  AU: 'en-AU',
  NZ: 'en-NZ',
  SG: 'en-SG',
  GB: 'en-GB',
  US: 'en-US',
};

export function defaultCountry(): string {
  const iso = String(environment.defaultCountry || 'IN').toUpperCase();
  return /^[A-Z]{2}$/.test(iso) ? iso : 'IN';
}

/** The logged-in tenant's country, else the build default. */
export function currentCountry(): string {
  try {
    const stored = JSON.parse(localStorage.getItem('organizationGroup') || 'null');
    const iso = (stored?.data?.countryCode || stored?.countryCode || '').toUpperCase();
    if (/^[A-Z]{2}$/.test(iso)) {
      return iso;
    }
  } catch {
    // fall through to the build default
  }
  return defaultCountry();
}

/** Accepts an ISO country ('AU') or a dial code ('+61'); defaults to the current country. */
export function countryLocale(country?: string | null): CountryLocale {
  let iso = (country ?? '').trim().toUpperCase();
  if (iso.startsWith('+')) {
    iso = (swappedPhoneCode as any)[iso] || '';
  }
  if (!/^[A-Z]{2}$/.test(iso)) {
    iso = currentCountry();
  }
  const currency = (countryCurrencyContent as any)[iso] || (countryCurrencyContent as any)[defaultCountry()];
  return {
    countryIso: iso,
    dialCode: (phoneCode as any)[iso] || '+91',
    timeZone: TIME_ZONES[iso] || TIME_ZONES[defaultCountry()] || 'UTC',
    locale: LOCALES[iso] || 'en-GB',
    currency: currency?.code || 'INR',
    currencySymbol: currency?.symbol || '₹',
  };
}

/**
 * Normalise a typed phone number to E.164 with the given dial code, matching the
 * backend's normalisePhoneNumber:
 *   '0412 345 678', '+61' -> '+61412345678'
 *   '9876543210',   '+91' -> '+919876543210'
 * Returns null when the result isn't 8-15 digits.
 */
export function normalisePhoneNumber(raw: string, dialCode: string = countryLocale().dialCode): string | null {
  if (!raw) {
    return null;
  }
  const trimmed = String(raw).trim();
  const hasPlus = trimmed.startsWith('+') || trimmed.startsWith('00');
  let digits = trimmed.replace(/\D/g, '');
  const cc = dialCode.replace(/\D/g, '');
  if (trimmed.startsWith('00')) {
    digits = digits.slice(2);
  }

  let e164: string;
  if (hasPlus) {
    e164 = digits;
  } else if (digits.startsWith('0')) {
    e164 = cc + digits.replace(/^0+/, '');
  } else if (cc && digits.startsWith(cc) && digits.length - cc.length >= 9) {
    e164 = digits;
  } else {
    e164 = cc + digits;
  }
  return e164.length >= 8 && e164.length <= 15 ? `+${e164}` : null;
}

/** National part of an E.164 number for display, e.g. '+61412345678' -> '0412345678' for AU. */
export function nationalNumber(value: string, dialCode: string = countryLocale().dialCode): string {
  if (!value) {
    return value;
  }
  const str = String(value);
  if (!str.startsWith(dialCode)) {
    return str.startsWith('+') ? str.slice(1) : str;
  }
  const rest = str.slice(dialCode.length);
  // Australian and other trunk-prefix countries show the leading 0; India doesn't
  return dialCode === '+91' ? rest : `0${rest}`;
}

/** Pattern for the national part of a mobile number typed into a form. */
export function nationalPhonePattern(country?: string | null): RegExp {
  const {countryIso} = countryLocale(country);
  if (countryIso === 'IN') {
    return /^[6-9]\d{9}$/;
  }
  if (countryIso === 'AU') {
    return /^0?\d{9}$/;
  }
  return /^\d{6,14}$/;
}

/** 'YYYY-MM-DD' for today in the given time zone. */
export function todayIn(timeZone: string = countryLocale().timeZone): string {
  return new Date().toLocaleDateString('en-CA', {timeZone});
}
